import React, { useState, useEffect } from 'react';
import { cleanupTooltips } from '../utils/tooltipCleanup';
import { useTheme } from '../context/ThemeContext';
import { 
  getRecentKabilDevelopments, 
  getKabilSummary,
  getCountryFlagCode,
  getStageColor,
  getKabilDeals
} from '../services/kabilApi';
import Flag from 'react-flagkit';
import { getCountryPolicyDetails, getPolicySummary } from '../services/policyDetailsApi';
import { 
  getPestleNewsSummary,
  getPestleArticlesByMineral,
  getPestleColor,
  getRiskLevelColor,
  getRiskLevelDotColor,
  formatArticleDate
} from '../services/pestleApi';
import MineralTreemap from '../components/MineralTreemap';
import { 
  getIeaPolicySummary,
  formatPolicyDate,
  getPolicyTypeColor
} from '../services/marketIntelligenceApi';

const PolicyIntelligencePage = () => {
  const { isDarkMode } = useTheme();
  const [kabilDevelopments, setKabilDevelopments] = useState([]);
  const [kabilSummary, setKabilSummary] = useState(null);
  const [kabilLoading, setKabilLoading] = useState(true);
  const [policyDetails, setPolicyDetails] = useState(null);
  const [policySummary, setPolicySummary] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [showPolicyDetails, setShowPolicyDetails] = useState(false);
  
  // PESTLE News states
  const [pestleNewsSummary, setPestleNewsSummary] = useState(null);
  const [pestleLoading, setPestleLoading] = useState(true);
  
  // Selected mineral and its PESTLE news
  const [selectedMineral, setSelectedMineral] = useState(null);
  const [mineralPestleNews, setMineralPestleNews] = useState([]);
  const [mineralNewsLoading, setMineralNewsLoading] = useState(false);
  
  // IEA Policy states
  const [ieaPolicySummary, setIeaPolicySummary] = useState(null);
  const [ieaLoading, setIeaLoading] = useState(true);
  
  // Tab navigation state
  const [activeTab, setActiveTab] = useState('mou-tracker');
  
  const tabs = [
    { id: 'mou-tracker', label: 'KAABIL/MoU Tracker'},
    { id: 'pestle-news', label: 'Critical Minerals News Monitor'}
  ];

  // Load KABIL data
  useEffect(() => {
    const loadKabilData = async () => {
      try {
        setKabilLoading(true);
        const [developments, summary] = await Promise.all([
          getRecentKabilDevelopments(),
          getKabilSummary()
        ]);
        setKabilDevelopments(developments);
        setKabilSummary(summary);
      } catch (error) {
        console.error('Error loading KABIL data:', error);
      } finally {
        setKabilLoading(false);
      }
    };

    loadKabilData();
  }, []);

  // Load policy data
  useEffect(() => {
    const loadPolicyData = async () => {
      try {
        setPolicyLoading(true);
        // Get recent policy details without specific filters to get general policy data
        const [details, summary] = await Promise.all([
          getCountryPolicyDetails(), // This will return empty array if no params
          getPolicySummary() // This will return empty object if no params
        ]);
        setPolicyDetails(details);
        setPolicySummary(summary);
      } catch (error) {
        console.error('Error loading policy data:', error);
        // Set empty data instead of fallback
        setPolicyDetails([]);
        setPolicySummary(null);
      } finally {
        setPolicyLoading(false);
      }
    };

    loadPolicyData();
  }, []);

  // Load PESTLE news data
  useEffect(() => {
    const loadPestleData = async () => {
      try {
        setPestleLoading(true);
        const summary = await getPestleNewsSummary();
        setPestleNewsSummary(summary);
      } catch (error) {
        console.error('Error loading PESTLE news data:', error);
        setPestleNewsSummary(null);
      } finally {
        setPestleLoading(false);
      }
    };

    loadPestleData();
  }, []);

  // Load IEA policy data
  useEffect(() => {
    const loadIeaData = async () => {
      try {
        setIeaLoading(true);
        const summary = await getIeaPolicySummary();
        setIeaPolicySummary(summary);
      } catch (error) {
        console.error('Error loading IEA policy data:', error);
        setIeaPolicySummary(null);
      } finally {
        setIeaLoading(false);
      }
    };

    loadIeaData();
  }, []);

  // Handle mineral selection from treemap
  const handleMineralSelect = async (mineral) => {
    setSelectedMineral(mineral);
    setMineralNewsLoading(true);
    
    try {
      const news = await getPestleArticlesByMineral(mineral, 20);
      setMineralPestleNews(news);
    } catch (error) {
      console.error('Error fetching PESTLE news for mineral:', error);
      setMineralPestleNews([]);
    } finally {
      setMineralNewsLoading(false);
    }
  };

  // Cleanup tooltips when component unmounts
  useEffect(() => {
    return () => {
      cleanupTooltips();
    };
  }, []);

  return (
    <div className={`w-full transition-colors duration-200 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      <div className="w-full px-4 py-8">
        {/* Header */}
        <div className="text-left mb-8">
          <h1 className={`text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Policy Intelligence Dashboard</h1>
          <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Strategic intelligence for critical minerals policy, partnerships, and market analysis
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400'
                      : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:border-gray-300' : 'text-gray-600 hover:text-gray-900 hover:border-gray-400'}`
                  }`}
                >
                  
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'mou-tracker' && (
            <div className="tab-panel">
              {/* MoU Tracker Section */}
              <div className={`rounded-lg p-6 border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            {kabilLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-2xl mb-2 animate-spin">🔄</div>
                  <div className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Loading MoU data...</div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">

                {/* KABIL Recent Geopolitical Developments */}
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent KAABIL/MoU Geopolitical Developments</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'text-gray-400 bg-gray-700' : 'text-gray-600 bg-gray-200'}`}>
                      {kabilDevelopments.length}
                    </span>
                  </div>
                </div>
                <p>This section tracks key government collaborations and agreements for critical minerals. It highlights partnerships, MoUs, and initiatives that strengthen India’s access to global mineral resources. </p>
                <div className={`mb-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Source: PIB and KABIL India
                </div>
                
                {kabilDevelopments.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {kabilDevelopments.map((event, index) => (
                      <div key={event.id || index} className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                        <div className="text-amber-400 text-sm mb-2">{event.formattedDate || event.date}</div>
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-12 rounded shadow-sm overflow-hidden flex items-center justify-center">
                            {(() => {
                              // Try to use ISO3 code first, then fall back to country name
                              const countryCode = getCountryFlagCode(event.iso3 || event.country);
                              console.log('Event data:', event); // Debug log
                              console.log('Country:', event.country, 'ISO3:', event.iso3, 'Code:', countryCode); // Debug log
                              
                              // Check if we have a valid country code and it's not UN
                              if (countryCode && countryCode !== 'UN') {
                                try {
                                  return <Flag country={countryCode} size={64} />;
                                } catch (error) {
                                  console.error('Error rendering flag for country code:', countryCode, error);
                                  // Fallback to text display
                                }
                              }
                              
                              // Fallback to text display
                              return (
                                <div className="w-full h-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-400">
                                  {event.country ? event.country.substring(0, 2).toUpperCase() : '??'}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="flex-1">
                            <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              India {(() => {
                                const stageMap = {
                                  'JV Signed': 'signs MoU',
                                  'MoU': 'signs MoU', 
                                  'Due Diligence': 'negotiates MoU',
                                  'Exploration': 'explores MoU',
                                  'Feasibility Study': 'studies MoU',
                                  'Mining License': 'secures license',
                                  'Production': 'commences production'
                                };
                                return stageMap[event.stage] || 'engages';
                              })()} with {event.partner} on critical mineral {Array.isArray(event.minerals) ? event.minerals.join(', ') : event.minerals || 'Critical Minerals'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <div className="text-lg mb-2">🤝</div>
                    <div>No recent KABIL developments</div>
                    <div className="text-xs mt-1">
                      New partnerships will appear here as they are established
                    </div>
                  </div>
                )}
              </div>
            )}
              </div>
            </div>
          )}

          {activeTab === 'pestle-news' && (
            <div className="tab-panel">
              {/* PESTLE News Section */}
              <div className={`rounded-lg p-6 border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            {pestleLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-2xl mb-2 animate-spin">🔄</div>
                  <div className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Loading PESTLE news analysis...</div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">


                {/* Recent PESTLE Articles */}
                {pestleNewsSummary && pestleNewsSummary.recentArticles?.length > 0 ? (
                  <div className="space-y-4">
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent News</h3>
                    <p> This tracker monitors global and domestic developments affecting critical minerals through political, economic, social, technological, legal, and environmental lenses. 
                    It highlights trends, policies, and risks shaping the critical minerals ecosystem. </p>
                    <div className="space-y-3">
                      {pestleNewsSummary.recentArticles.slice(0, 5).map((article, index) => (
                        <div key={article.id || index} className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${
                              getRiskLevelDotColor(article.impact_level || article.pestle_risk_level)
                            }`}></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatArticleDate(article.date)}</div>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  getRiskLevelColor(article.impact_level || article.pestle_risk_level)
                                }`}>
                                  {article.impact_level || article.pestle_risk_level || 'Medium'}
                                </span>
                              </div>
                              <div className={`text-sm mb-2 line-clamp-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}><h2>{article.headline || 'PESTLE Analysis Article'}</h2></div>
                              <div className="flex items-center gap-2">
                                {article.pestle_category && (
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    article.pestle_category === 'Economic' ? (isDarkMode ? 'bg-yellow-800 text-yellow-100' : 'bg-yellow-100 text-yellow-800') :
                                    article.pestle_category === 'Environmental' ? (isDarkMode ? 'bg-green-800 text-green-100' : 'bg-green-100 text-green-800') :
                                    article.pestle_category === 'Political' ? (isDarkMode ? 'bg-red-800 text-red-100' : 'bg-red-100 text-red-800') :
                                    article.pestle_category === 'Social' ? (isDarkMode ? 'bg-blue-800 text-blue-100' : 'bg-blue-100 text-blue-800') :
                                    article.pestle_category === 'Technological' ? (isDarkMode ? 'bg-purple-800 text-purple-100' : 'bg-purple-100 text-purple-800') :
                                    article.pestle_category === 'Legal' ? (isDarkMode ? 'bg-orange-800 text-orange-100' : 'bg-orange-100 text-orange-800') : 
                                    (isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-100 text-gray-800')
                                  }`}>
                                    {article.pestle_category}
                                  </span>
                                )}
                                {article.minerals && article.minerals.length > 0 && (
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
                                    {article.minerals[0]}
                                  </span>
                                )}
                              </div>
                              {/* Source Link */}
                              {article.source_url && (
                                <div className="mt-2">
                                  <a 
                                    href={article.source_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-400 hover:text-blue-300 underline"
                                  >
                                    Source
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="text-4xl mb-4">📰</div>
                    <div>No PESTLE news analysis available</div>
                  </div>
                )}

                {/* Mineral Treemap and PESTLE News Side by Side */}
                {pestleNewsSummary && pestleNewsSummary.topMinerals && pestleNewsSummary.topMinerals.length > 0 && (
                  <div className="mt-8">
                    <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      News Coverage by Mineral
                    </h3>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Showing coverage for: Lithium, Cobalt, Nickel, Graphite, and Rare Earth Elements
                    </p>
                    
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left side - Treemap */}
                      <div className="w-full">
                        <MineralTreemap 
                          data={pestleNewsSummary.topMinerals}
                          loading={pestleLoading}
                          error={null}
                          onMineralSelect={handleMineralSelect}
                        />
                      </div>
                      
                      {/* Right side - PESTLE News List */}
                      <div className="w-full">
                        {selectedMineral ? (
                          <div className="h-full">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                News for {selectedMineral}
                              </h3>
                              <button
                                onClick={() => setSelectedMineral(null)}
                                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                                  isDarkMode 
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                Clear Selection
                              </button>
                            </div>
                            
                            {mineralNewsLoading ? (
                              <div className="flex items-center justify-center h-64">
                                <div className="text-center">
                                  <div className="text-2xl mb-2 animate-spin">🔄</div>
                                  <div className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Loading PESTLE news for {selectedMineral}...</div>
                                </div>
                              </div>
                            ) : mineralPestleNews.length > 0 ? (
                              <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                                {mineralPestleNews.map((article, index) => (
                                  <div key={article.id || index} className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                                    <div className="flex items-start gap-3">
                                      <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${
                                        getRiskLevelDotColor(article.impact_level || article.pestle_risk_level)
                                      }`}></div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {formatArticleDate(article.date)}
                                          </div>
                                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            getRiskLevelColor(article.impact_level || article.pestle_risk_level)
                                          }`}>
                                            {article.impact_level || article.pestle_risk_level || 'Medium'}
                                          </span>
                                        </div>
                                        <div className={`text-sm mb-2 line-clamp-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                          <h4>{article.headline || 'PESTLE Analysis Article'}</h4>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {article.pestle_category && (
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                              article.pestle_category === 'Economic' ? (isDarkMode ? 'bg-yellow-800 text-yellow-100' : 'bg-yellow-100 text-yellow-800') :
                                              article.pestle_category === 'Environmental' ? (isDarkMode ? 'bg-green-800 text-green-100' : 'bg-green-100 text-green-800') :
                                              article.pestle_category === 'Political' ? (isDarkMode ? 'bg-red-800 text-red-100' : 'bg-red-100 text-red-800') :
                                              article.pestle_category === 'Social' ? (isDarkMode ? 'bg-blue-800 text-blue-100' : 'bg-blue-100 text-blue-800') :
                                              article.pestle_category === 'Technological' ? (isDarkMode ? 'bg-purple-800 text-purple-100' : 'bg-purple-100 text-purple-800') :
                                              article.pestle_category === 'Legal' ? (isDarkMode ? 'bg-orange-800 text-orange-100' : 'bg-orange-100 text-orange-800') : 
                                              (isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-100 text-gray-800')
                                            }`}>
                                              {article.pestle_category}
                                            </span>
                                          )}
                                          {article.countries_involved && article.countries_involved.length > 0 && (
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
                                              {article.countries_involved[0]}
                                            </span>
                                          )}
                                        </div>
                                        {/* Source Link */}
                                        {article.source_url && (
                                          <div className="mt-2">
                                            <a 
                                              href={article.source_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-xs text-blue-400 hover:text-blue-300 underline"
                                            >
                                              View Source
                                            </a>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <div className="text-4xl mb-4">📰</div>
                                <div>No PESTLE news found for {selectedMineral}</div>
                                <div className="text-xs mt-1">
                                  Try selecting a different mineral from the treemap
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <div className="text-4xl mb-4">📊</div>
                            <div className="text-lg font-medium mb-2">Select a Mineral</div>
                            <div className="text-sm">
                              Click on any mineral in the treemap to view its PESTLE news coverage
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyIntelligencePage;
